import { computed, reactive } from 'vue'

import { ApiError, request, setTokenProvider } from '@/api/client'
import { ApiErrorCode, type MeResponse, type PropertyInfo } from '@/api/types'
import { config } from '@/config'
import { es } from '@/locales/es'

import { createAuthClient, type AuthClient } from './msal'
import { hasPermissionIn } from './permissions'

/**
 * Who is using the app, and what they may do.
 *
 * Entra ID only authenticates; `/me` (PostgreSQL) says role, permissions and
 * plazas. The store has no dependency on Pinia: a reactive module singleton
 * is enough for one record read from many places, and tests can import it
 * without installing a plugin.
 *
 * Status:
 *   loading    startup, before MSAL / `/me` answered
 *   ready      session usable (also the permanent state of dev mode)
 *   anonymous  no session -> login screen
 *   denied     authenticated but not registered / inactive -> access request
 *   error      could not reach the API to read `/me`
 */
export type SessionStatus = 'loading' | 'ready' | 'anonymous' | 'denied' | 'error'
export type DenialReason = 'user_not_registered' | 'user_inactive'

interface SessionState {
  status: SessionStatus
  me: MeResponse | null
  /** True when Entra is not configured: no MSAL, no sign-in. */
  devMode: boolean
  denial: DenialReason | null
  /** Identity known to MSAL even when `/me` was refused (for the access-request screen). */
  account: { name: string; email: string } | null
  /** Message for the login screen. */
  error: string | null
}

function initialState(): SessionState {
  // No local fallback principal: who the user is, and which plazas they have,
  // only ever comes from `/me`. Dev mode (no Entra) asks the backend, which
  // answers as `dev-bypass` when it runs in development.
  return { status: 'loading', me: null, devMode: !config.isAuthConfigured, denial: null, account: null, error: null }
}

const state = reactive<SessionState>(initialState())

let client: AuthClient | null = null

// --- Reads -------------------------------------------------------------

export function hasPermission(code: string): boolean {
  return hasPermissionIn(state.me?.permissions, code)
}

export function useSession() {
  return {
    state,
    status: computed(() => state.status),
    me: computed(() => state.me),
    isDevMode: computed(() => state.devMode),
    isAuthenticated: computed(() => state.status === 'ready'),
    displayName: computed(() => state.me?.displayName || state.me?.email || ''),
    email: computed(() => state.me?.email ?? state.account?.email ?? ''),
    roleName: computed(() => state.me?.role?.name ?? ''),
    properties: computed<PropertyInfo[]>(() => state.me?.properties ?? []),
    allProperties: computed(() => state.me?.allProperties ?? false),
    canSeePrices: computed(() => hasPermission('maps:price:read')),
    can: hasPermission,
  }
}

// --- /me ---------------------------------------------------------------

/** Tolerant normalisation: a missing array must not crash the whole UI. */
export function normalizeMe(raw: Partial<MeResponse> & { role?: unknown }): MeResponse {
  const role = raw.role
  return {
    id: raw.id ?? '',
    email: raw.email ?? '',
    displayName: raw.displayName ?? '',
    role: { name: typeof role === 'string' ? role : ((role as { name?: string } | undefined)?.name ?? '') },
    permissions: Array.isArray(raw.permissions) ? raw.permissions : [],
    allProperties: !!raw.allProperties,
    properties: Array.isArray(raw.properties) ? raw.properties : [],
    authMode: raw.authMode === 'dev-bypass' || raw.authMode === 'entra-no-db' ? raw.authMode : 'entra',
  }
}

export async function fetchMe(timeoutMs?: number): Promise<MeResponse> {
  const me = normalizeMe(await request<Partial<MeResponse>>('/api/v1/me', { timeoutMs }))
  // `allProperties` users may come back without the list; the catalogue is
  // then read from /properties (needs maps:property:read).
  if (me.allProperties && me.properties.length === 0 && hasPermissionIn(me.permissions, 'maps:property:read')) {
    try {
      const all = (await request<PropertyInfo[] | null>('/api/v1/properties', { timeoutMs })) ?? []
      me.properties = all.map(({ code, name, location, hasPublishedPlan }) => ({ code, name, location, hasPublishedPlan: !!hasPublishedPlan }))
    } catch {
      // The viewer shows its "no plazas" state.
    }
  }
  return me
}

export function denialOf(error: unknown): DenialReason | null {
  if (
    error instanceof ApiError &&
    error.status === 403 &&
    (error.code === 'user_not_registered' || error.code === 'user_inactive')
  ) {
    return error.code
  }
  return null
}

/**
 * Message for the login screen when `/me` fails for a reason other than a
 * 401/403. An unreachable API (network error or timeout) names the address
 * that was tried, which is the first thing to check when the backend is down.
 */
export function describeLoadError(error: unknown): string {
  if (error instanceof ApiError && (error.code === ApiErrorCode.Network || error.code === ApiErrorCode.Timeout)) {
    return es.auth.errors.apiUnreachable.replace('{url}', config.apiBaseUrl || window.location.origin)
  }
  return error instanceof Error ? error.message : es.errors.generic
}

async function loadMe(): Promise<void> {
  try {
    state.me = await fetchMe()
    state.denial = null
    state.error = null
    state.status = 'ready'
  } catch (error) {
    const denial = denialOf(error)
    if (denial) {
      denyAccess(denial)
    } else if (error instanceof ApiError && error.status === 401) {
      state.status = 'anonymous'
      state.error = es.errors.unauthorized
    } else {
      state.status = 'error'
      state.error = describeLoadError(error)
    }
  }
}

/** The backend refused the account (403 user_not_registered / user_inactive). */
export function denyAccess(reason: DenialReason): void {
  state.me = null
  state.denial = reason
  state.status = 'denied'
}

/** A 401 survived the silent renewal: back to the login screen. */
export function markExpired(): void {
  if (state.devMode || state.status !== 'ready') return
  state.me = null
  state.status = 'anonymous'
  state.error = es.errors.unauthorized
}

// --- Startup -----------------------------------------------------------

/** Dev mode: ask the API who we are. No answer means the connection error screen: never fake data. */
async function initDev(): Promise<void> {
  try {
    state.me = await fetchMe(5000)
    state.error = null
    state.status = 'ready'
  } catch (error) {
    state.me = null
    state.status = 'error'
    state.error = describeLoadError(error)
  }
}

async function initEntra(): Promise<void> {
  try {
    client = await createAuthClient()
  } catch (error) {
    state.status = 'error'
    state.error = error instanceof Error ? error.message : es.errors.generic
    return
  }
  setTokenProvider(client.getToken)

  const { account, error } = await client.init()
  if (error) state.error = es.auth.errors.loginFailed
  if (!account) {
    state.status = 'anonymous'
    return
  }
  state.account = { name: account.name ?? account.username, email: account.username }
  await loadMe()
}

export async function initSession(): Promise<void> {
  if (config.isAuthConfigured) await initEntra()
  else await initDev()
}

// --- Actions -----------------------------------------------------------

/** Sends the user to Microsoft; resolves only if the redirect could not start. */
export async function signIn(redirectTo?: string): Promise<void> {
  // Dev mode has no Microsoft: "retry" just asks the backend again.
  if (state.devMode) {
    state.error = null
    await initDev()
    return
  }
  if (!client) return
  state.error = null
  // The API may simply have been down: retry `/me` before bothering Microsoft.
  if (state.status === 'error' && client.account()) {
    await loadMe()
    if ((state.status as SessionStatus) === 'ready') return
  }
  try {
    await client.login(redirectTo)
  } catch (error) {
    state.error = error instanceof Error ? error.message : es.auth.errors.loginFailed
  }
}

export async function signOut(): Promise<void> {
  if (!client) return
  await client.logout()
}

/** Test helper: replaces the whole state. */
export function __setSessionForTests(next: Partial<SessionState>): void {
  Object.assign(state, initialState(), next)
}
